import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Button, TextField, CircularProgress, useMediaQuery,
} from '@mui/material';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useToast } from '../../providers/toaster-provider';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

export interface FolderNameProps {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    entityId: number | null;
    mode: 'create' | 'rename';
    // 'create': the new folder's parent. 'rename': the folder being renamed.
    parentFolderId?: number | null;
    folderId?: number | null;
    initialName?: string;
}

const FolderName: React.FC<FolderNameProps> = ({ open, onClose, onSaved, entityId, mode, parentFolderId, folderId, initialName }) => {
    const { showError, showSuccess } = useToast();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [name, setName] = useState('');
    const [createFolder, setCreateFolder] = useApi();
    const [updateFolder, setUpdateFolder] = useApi();

    useEffect(() => {
        if (open) setName(mode === 'rename' ? (initialName || '') : '');
    }, [open, mode, initialName]);

    const isSaving = createFolder.isLoading || updateFolder.isLoading;

    const handleSave = () => {
        if (!name.trim() || !entityId) return;
        if (mode === 'create') {
            setCreateFolder({
                ...buildApiRequestConfig(routes.documentFolders.create),
                body: { entityId, name: name.trim(), parentFolderId: parentFolderId ?? null },
            });
        } else if (folderId) {
            setUpdateFolder({
                ...buildApiRequestConfig(routes.documentFolders.update, [{ param: 'id', value: folderId }]),
                body: { entityId, name: name.trim() },
            });
        }
    };

    useEffect(() => {
        const { data, isLoading, error } = createFolder;
        if (!isLoading && data) { showSuccess('Folder created'); onSaved(); onClose(); }
        if (error) showError(typeof error === 'string' ? error : 'Failed to create folder');
    }, [createFolder.data, createFolder.isLoading, createFolder.error]);

    useEffect(() => {
        const { data, isLoading, error } = updateFolder;
        if (!isLoading && data) { showSuccess('Folder renamed'); onSaved(); onClose(); }
        if (error) showError(typeof error === 'string' ? error : 'Failed to rename folder');
    }, [updateFolder.data, updateFolder.isLoading, updateFolder.error]);

    const content = (
        <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 1 }}>
                {mode === 'create' ? 'New Folder' : 'Rename Folder'}
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Box sx={{ pt: 1 }}>
                    <TextField
                        autoFocus
                        fullWidth size="small"
                        placeholder="Folder name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSave()}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 110, '&:hover': { background: 'var(--color-container-background)' } }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!name.trim() || isSaving}
                    startIcon={isSaving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined}
                    sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 110, '&:hover': { background: 'var(--color-primary-hover)' } }}
                >
                    {mode === 'create' ? 'Create' : 'Save'}
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={onClose} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default FolderName;
